// Campaign identity and first-touch attribution data.
//
// docs/CAMPAIGN_52_FINAL_ARCHITECTURE.md §3 makes kgob.com responsible for
// minting a `vid` on page load, carrying it plus UTMs onto BOTH outbound CTAs,
// and writing the bridge doc described in §4-A. This module holds the pieces of
// that contract that are not tied to a single component.

/** §4-D: the single campaign discriminator. A future campaign is a new slug. */
export const CAMPAIGN_SLUG = '52-time-audit';

/** §4-A: `source_app` on _bridge_meta. We are the kgob.com side. */
export const SOURCE_APP = 'kgob.com';

const LP_META_KEY = 'kgob52.lp_meta';
const VID_KEY = 'kgob52.vid';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'fbclid', 'msclkid'];

const session = () => {
  try { return window.sessionStorage; } catch { return null; }   // private mode / blocked storage
};

/**
 * §4-B: the vid stitches the anonymous pre-submit hops, so it must be the SAME
 * id from the landing page through to whichever CTA the prospect clicks — a new
 * one per page would break the join. Held in sessionStorage so a reload or a
 * direct hit on /52/results keeps it.
 *
 * NOTE (open decision §7-G): format is uuid-vs-short-id, still to be agreed
 * across the three teams. This mints 12 hex characters. If the agreed format
 * differs, change it HERE only — every other reference reads this value.
 */
export function getVid() {
  const s = session();
  const existing = s && s.getItem(VID_KEY);
  if (existing) return existing;

  const c = globalThis.crypto;
  let v;
  if (c?.randomUUID) v = c.randomUUID().replace(/-/g, '').slice(0, 12);
  else if (c?.getRandomValues) v = Array.from(c.getRandomValues(new Uint8Array(6)), (b) => b.toString(16).padStart(2, '0')).join('');
  else v = (Date.now().toString(36) + Math.random().toString(36).slice(2)).slice(0, 12);

  try { s && s.setItem(VID_KEY, v); } catch { /* no action needed */ }
  return v;
}

/**
 * §4-A `lp_meta`: first touch wins. Captured on the first /52 page load and kept
 * for the session, so a prospect who arrives on a UTM-tagged link, wanders, and
 * submits later is still attributed to the campaign that brought them.
 */
export function captureLpMeta() {
  const s = session();
  const stored = s && s.getItem(LP_META_KEY);
  if (stored) { try { return JSON.parse(stored); } catch { /* no action needed */ } }

  const q = new URLSearchParams(window.location.search);
  const meta = { referrer: document.referrer || '', landing_page_url: window.location.href, user_agent: navigator.userAgent };
  for (const k of UTM_KEYS) { const v = q.get(k); if (v) meta[k] = v; }

  try { s && s.setItem(LP_META_KEY, JSON.stringify(meta)); } catch { /* no action needed */ }
  return meta;
}

export function getLpMeta() {
  const s = session();
  try { return JSON.parse((s && s.getItem(LP_META_KEY)) || '{}'); } catch { return {}; }
}

/**
 * §3.4: both outbound CTAs carry the vid and the UTMs. The campaign's own
 * source/medium are defaults — a real inbound utm_source (an ad, a partner)
 * overrides them, so the downstream side sees where the prospect actually came
 * from rather than a value we hardcoded.
 */
export function outboundParams(vid, extra = {}) {
  const meta = getLpMeta();
  const p = new URLSearchParams({
    vid,
    utm_source: meta.utm_source || SOURCE_APP,
    utm_medium: meta.utm_medium || 'landing',
    utm_campaign: meta.utm_campaign || CAMPAIGN_SLUG,
  });
  for (const k of ['utm_term', 'gclid', 'fbclid', 'msclkid']) if (meta[k]) p.set(k, meta[k]);
  for (const [k, v] of Object.entries(extra)) if (v) p.set(k, v);
  return p.toString();
}
